import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import type { Workday } from '../../api/types';
import { AsyncButton } from '../../components/AsyncButton';
import { Modal } from '../../components/Modal';
import { fmtDateISO, fmtHM, fmtTime, todayISO, ymdLocal } from '../../utils/time';
import { computeDurationsClient } from '../../utils/workday';

type AbsenceLabel = 'Ferie/permesso' | 'Ferie' | 'Permesso';

type DayKind = 'WORKING' | 'SATURDAY' | 'SUNDAY' | 'HOLIDAY';

type MonthDay = {
  ymd: string;
  date: Date;
  kind: DayKind;
  holidayName: string | null;
  isWorkingDay: boolean;
  workday: Workday | null;

  expectedMin: number;
  workedMin: number;
  breakMin: number;
  absenceMin: number;
  overtimeMin: number;
};

function isoYYYYMMDD(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
}

function currentMonthValue() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');

  return `${yyyy}-${mm}`;
}

function startOfMonthFromValue(monthValue: string) {
  const [yyyy, mm] = monthValue.split('-').map(Number);

  return new Date(yyyy, mm - 1, 1);
}

function addMonthsISO(monthValue: string, months = 1) {
  const d = startOfMonthFromValue(monthValue);

  d.setMonth(d.getMonth() + months);

  return isoYYYYMMDD(d);
}

function monthDays(monthValue: string) {
  const first = startOfMonthFromValue(monthValue);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);

  const days: Date[] = [];
  const cur = new Date(first);

  while (cur.getTime() <= last.getTime()) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }

  return days;
}

function minutesFromHours(hours: number) {
  return Math.round(hours * 60);
}

function safeNumber(value: string | number, fallback: number) {
  const n = Number(value);

  return Number.isFinite(n) ? n : fallback;
}

function fmtDecimalHours(totalMin: number) {
  return (totalMin / 60).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function addDays(d: Date, days: number) {
  const x = new Date(d);

  x.setDate(x.getDate() + days);

  return x;
}

function easterSunday(year: number) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(year, month - 1, day);
}

function italianHolidays(year: number) {
  const map = new Map<string, string>();

  map.set(`${year}-01-01`, 'Capodanno');
  map.set(`${year}-01-06`, 'Epifania');
  map.set(`${year}-04-25`, 'Liberazione');
  map.set(`${year}-05-01`, 'Festa del lavoro');
  map.set(`${year}-06-02`, 'Festa della Repubblica');
  map.set(`${year}-08-15`, 'Ferragosto');
  map.set(`${year}-11-01`, 'Ognissanti');
  map.set(`${year}-12-08`, 'Immacolata');
  map.set(`${year}-12-25`, 'Natale');
  map.set(`${year}-12-26`, 'Santo Stefano');

  const pasquetta = addDays(easterSunday(year), 1);
  map.set(isoYYYYMMDD(pasquetta), 'Pasquetta');

  return map;
}

function parseExtraHolidays(value: string) {
  const map = new Map<string, string>();

  value
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
    .forEach((ymd) => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
        map.set(ymd, 'Festivo extra');
      }
    });

  return map;
}

function buildHolidayMap(monthValue: string, extraHolidays: string) {
  const first = startOfMonthFromValue(monthValue);
  const year = first.getFullYear();

  const map = new Map<string, string>();

  for (const [k, v] of italianHolidays(year)) {
    map.set(k, v);
  }

  for (const [k, v] of parseExtraHolidays(extraHolidays)) {
    map.set(k, v);
  }

  return map;
}

function dayKind(date: Date, holidayName: string | null): DayKind {
  const day = date.getDay();

  if (holidayName) return 'HOLIDAY';
  if (day === 6) return 'SATURDAY';
  if (day === 0) return 'SUNDAY';

  return 'WORKING';
}

function kindLabel(kind: DayKind, holidayName: string | null) {
  if (kind === 'HOLIDAY') return holidayName || 'Festivo';
  if (kind === 'SATURDAY') return 'Sabato';
  if (kind === 'SUNDAY') return 'Domenica';

  return 'Lavorativo';
}

function dayCardStyle(kind: DayKind, hasWorkday: boolean) {
  if (hasWorkday) {
    return {
      border: '1px solid rgba(60, 180, 90, .45)',
      background: 'rgba(60, 180, 90, .10)'
    };
  }

  if (kind === 'HOLIDAY') {
    return {
      border: '1px solid rgba(255, 180, 0, .35)',
      background: 'rgba(255, 180, 0, .12)'
    };
  }

  if (kind === 'SATURDAY' || kind === 'SUNDAY') {
    return {
      border: '1px solid var(--border)',
      background: 'rgba(255,255,255,.035)'
    };
  }

  return {
    border: '1px solid rgba(255, 80, 80, .30)',
    background: 'rgba(255, 80, 80, .08)'
  };
}

export function MonthlyReportPage() {
  const [month, setMonth] = useState(() => currentMonthValue());
  const [contractMode, setContractMode] = useState<'7' | '8' | 'custom'>('8');
  const [customContractHours, setCustomContractHours] = useState('8');
  const [absenceLabel, setAbsenceLabel] = useState<AbsenceLabel>('Ferie/permesso');
  const [extraHolidays, setExtraHolidays] = useState('');

  const [items, setItems] = useState<Workday[]>([]);
  const [selectedDay, setSelectedDay] = useState<MonthDay | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const today = todayISO();

  const contractHours = useMemo(() => {
    if (contractMode === '7') return 7;
    if (contractMode === '8') return 8;

    const n = safeNumber(customContractHours, 8);

    return Math.max(0, n);
  }, [contractMode, customContractHours]);

  const contractMin = useMemo(() => {
    return minutesFromHours(contractHours);
  }, [contractHours]);

  const range = useMemo(() => {
    return {
      from: `${month}-01`,
      to: addMonthsISO(month, 1)
    };
  }, [month]);

  async function load() {
    const r = await api.get<{ items: Workday[] }>(
      `/api/me/workdays?from=${range.from}&to=${range.to}`
    );

    setItems(r.items || []);
  }

  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const workdayByDate = useMemo(() => {
    const map = new Map<string, Workday>();

    for (const w of items) {
      map.set(ymdLocal(w.work_date), w);
    }

    return map;
  }, [items]);

  const holidayMap = useMemo(() => {
    return buildHolidayMap(month, extraHolidays);
  }, [month, extraHolidays]);

  const days = useMemo<MonthDay[]>(() => {
    return monthDays(month).map((date) => {
      const ymd = isoYYYYMMDD(date);
      const holidayName = holidayMap.get(ymd) || null;
      const kind = dayKind(date, holidayName);
      const isWorkingDay = kind === 'WORKING';
      const workday = workdayByDate.get(ymd) || null;

      const dur = workday ? computeDurationsClient(workday, new Date()) : null;

      const workedMin = dur?.netMin || 0;
      const breakMin = dur?.breakMin || 0;

      let expectedMin = 0;
      let absenceMin = 0;
      let overtimeMin = 0;

      if (isWorkingDay) {
        expectedMin = contractMin;
        absenceMin = Math.max(0, contractMin - workedMin);
        overtimeMin = Math.max(0, workedMin - contractMin);
      }

      if (!isWorkingDay && workedMin > 0) {
        expectedMin = 0;
        absenceMin = 0;
        overtimeMin = workedMin;
      }

      return {
        ymd,
        date,
        kind,
        holidayName,
        isWorkingDay,
        workday,
        expectedMin,
        workedMin,
        breakMin,
        absenceMin,
        overtimeMin
      };
    });
  }, [month, holidayMap, workdayByDate, contractMin]);

  const totals = useMemo(() => {
    const workingDays = days.filter((d) => d.isWorkingDay).length;
    const holidays = days.filter((d) => d.kind === 'HOLIDAY').length;
    const weekends = days.filter((d) => d.kind === 'SATURDAY' || d.kind === 'SUNDAY').length;

    const expectedMin = days.reduce((sum, d) => sum + d.expectedMin, 0);
    const workedMin = days.reduce((sum, d) => sum + d.workedMin, 0);
    const breakMin = days.reduce((sum, d) => sum + d.breakMin, 0);
    const absenceMin = days.reduce((sum, d) => sum + d.absenceMin, 0);
    const overtimeMin = days.reduce((sum, d) => sum + d.overtimeMin, 0);

    const fullAbsenceDays = days.filter((d) => d.isWorkingDay && !d.workday).length;
    const partialAbsenceDays = days.filter((d) => d.isWorkingDay && d.workday && d.absenceMin > 0).length;

    return {
      workingDays,
      holidays,
      weekends,
      expectedMin,
      workedMin,
      breakMin,
      absenceMin,
      overtimeMin,
      fullAbsenceDays,
      partialAbsenceDays
    };
  }, [days]);

  function openDay(day: MonthDay) {
    setSelectedDay(day);
    setDetailOpen(true);
  }

  function previousMonth() {
    const d = startOfMonthFromValue(month);

    d.setMonth(d.getMonth() - 1);

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');

    setMonth(`${yyyy}-${mm}`);
  }

  function nextMonth() {
    const d = startOfMonthFromValue(month);

    d.setMonth(d.getMonth() + 1);

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');

    setMonth(`${yyyy}-${mm}`);
  }

  return (
    <div
      className="grid"
      style={{
        gap: 14,
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="h2" style={{ marginTop: 0, marginBottom: 4 }}>
          Resoconto del mese
        </div>

        <div className="small">
          Riepilogo mensile delle tue ore lavorate, pause, {absenceLabel.toLowerCase()} e straordinari.
        </div>

        <div
          className="grid"
          style={{
            gridTemplateColumns: '1fr',
            gap: 10,
            marginTop: 14
          }}
        >
          <div>
            <div className="small">
              Mese
            </div>

            <input
              className="input"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <div className="small">
              Ore da contratto al giorno
            </div>

            <select
              className="input"
              value={contractMode}
              onChange={(e) => setContractMode(e.target.value as '7' | '8' | 'custom')}
              style={{ width: '100%', boxSizing: 'border-box' }}
            >
              <option value="7">7 ore</option>
              <option value="8">8 ore</option>
              <option value="custom">Personalizzato</option>
            </select>
          </div>

          {contractMode === 'custom' ? (
            <div>
              <div className="small">
                Ore personalizzate
              </div>

              <input
                className="input"
                type="number"
                min="0"
                step="0.25"
                value={customContractHours}
                onChange={(e) => setCustomContractHours(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          ) : null}

          <div>
            <div className="small">
              Assenze da mostrare come
            </div>

            <select
              className="input"
              value={absenceLabel}
              onChange={(e) => setAbsenceLabel(e.target.value as AbsenceLabel)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            >
              <option value="Ferie/permesso">Ferie/permesso</option>
              <option value="Ferie">Ferie</option>
              <option value="Permesso">Permesso</option>
            </select>
          </div>

          <details>
            <summary style={{ cursor: 'pointer', fontWeight: 900 }}>
              Festivi extra
            </summary>

            <div className="small" style={{ marginTop: 8 }}>
              Inserisci eventuali festivi locali separati da virgola.
            </div>

            <input
              className="input"
              placeholder="es. 2026-09-19, 2026-12-24"
              value={extraHolidays}
              onChange={(e) => setExtraHolidays(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 8 }}
            />
          </details>
        </div>

        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
          <button className="btn btnGhost" type="button" onClick={previousMonth}>
            ← Prec.
          </button>

          <AsyncButton
            className="btn btnPrimary"
            onClickAsync={load}
            busyText="Aggiorno…"
          >
            Aggiorna
          </AsyncButton>

          <button className="btn btnGhost" type="button" onClick={nextMonth}>
            Succ. →
          </button>
        </div>
      </div>

      <div
        className="grid"
        style={{
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 10
        }}
      >
        <SummaryCard
          label="Contratto"
          value={fmtHM(totals.expectedMin)}
          sub={`${totals.workingDays} giorni`}
        />

        <SummaryCard
          label="Lavorate"
          value={fmtHM(totals.workedMin)}
          sub={`${fmtDecimalHours(totals.workedMin)} ore`}
        />

        <SummaryCard
          label="Pause"
          value={fmtHM(totals.breakMin)}
          sub={`${fmtDecimalHours(totals.breakMin)} ore`}
        />

        <SummaryCard
          label={absenceLabel}
          value={fmtHM(totals.absenceMin)}
          sub={`${totals.fullAbsenceDays} giorni · ${totals.partialAbsenceDays} parziali`}
        />

        <SummaryCard
          label="Straordinario"
          value={fmtHM(totals.overtimeMin)}
          sub={`${fmtDecimalHours(totals.overtimeMin)} ore`}
        />

        <SummaryCard
          label="Non lavorativi"
          value={String(totals.weekends + totals.holidays)}
          sub={`${totals.weekends} sab/dom · ${totals.holidays} festivi`}
        />
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="spread" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <div>
            <div className="h3" style={{ margin: 0 }}>
              Giorni del mese
            </div>

            <div className="small">
              Tocca un giorno per il dettaglio.
            </div>
          </div>

          <div className="badge badgeGray">
            {contractHours.toLocaleString('it-IT')}h/giorno
          </div>
        </div>

        <div className="grid" style={{ gap: 10 }}>
          {days.map((d) => (
            <button
              key={d.ymd}
              type="button"
              onClick={() => openDay(d)}
              className="card"
              style={{
                ...dayCardStyle(d.kind, Boolean(d.workday)),
                boxShadow: 'none',
                textAlign: 'left',
                padding: 12,
                display: 'grid',
                gap: 8
              }}
            >
              <div className="spread" style={{ gap: 10, alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 16 }}>
                    {fmtDateISO(d.ymd)}
                  </div>

                  <div className="small" style={{ marginTop: 3 }}>
                    {kindLabel(d.kind, d.holidayName)}
                  </div>
                </div>

                {d.ymd === today ? (
                  <span className="badge badgeGray">
                    Oggi
                  </span>
                ) : null}
              </div>

              {d.workday ? (
                <div
                  className="grid"
                  style={{
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 8
                  }}
                >
                  <MiniStat label="Netto" value={fmtHM(d.workedMin)} />
                  <MiniStat label="Pausa" value={fmtHM(d.breakMin)} />
                  <MiniStat label="Extra" value={fmtHM(d.overtimeMin)} />
                </div>
              ) : d.isWorkingDay ? (
                <div>
                  <div style={{ fontWeight: 900 }}>
                    Nessuna timbratura
                  </div>

                  <div className="small">
                    {absenceLabel}: <b>{fmtHM(d.absenceMin)}</b>
                  </div>
                </div>
              ) : (
                <div className="small">
                  Giorno non lavorativo
                </div>
              )}

              {d.workday && d.absenceMin > 0 ? (
                <div className="small">
                  {absenceLabel}: <b>{fmtHM(d.absenceMin)}</b>
                </div>
              ) : null}

              {d.overtimeMin > 0 ? (
                <div className="small">
                  Straordinario: <b>{fmtHM(d.overtimeMin)}</b>
                </div>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      <Modal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={selectedDay ? `Dettaglio · ${fmtDateISO(selectedDay.ymd)}` : 'Dettaglio'}
      >
        {selectedDay ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="card" style={{ boxShadow: 'none', border: '1px solid var(--border)' }}>
              <div className="small">
                Tipo giorno
              </div>

              <div style={{ fontSize: 22, fontWeight: 900 }}>
                {kindLabel(selectedDay.kind, selectedDay.holidayName)}
              </div>
            </div>

            <div
              className="grid"
              style={{
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 10
              }}
            >
              <SummaryCard
                label="Contratto"
                value={fmtHM(selectedDay.expectedMin)}
              />

              <SummaryCard
                label="Lavorate"
                value={fmtHM(selectedDay.workedMin)}
              />

              <SummaryCard
                label="Pausa"
                value={fmtHM(selectedDay.breakMin)}
              />

              <SummaryCard
                label={absenceLabel}
                value={fmtHM(selectedDay.absenceMin)}
              />

              <SummaryCard
                label="Straordinario"
                value={fmtHM(selectedDay.overtimeMin)}
              />
            </div>

            <div className="card" style={{ boxShadow: 'none', border: '1px solid var(--border)' }}>
              <div className="h3" style={{ marginTop: 0 }}>
                Timbratura
              </div>

              {selectedDay.workday ? (
                <div className="grid" style={{ gap: 10 }}>
                  <DetailRow label="Inizio lavoro" value={fmtTime(selectedDay.workday.start_work_at)} />
                  <DetailRow label="Inizio pausa" value={fmtTime(selectedDay.workday.start_break_at)} />
                  <DetailRow label="Fine pausa" value={fmtTime(selectedDay.workday.end_break_at)} />
                  <DetailRow label="Fine lavoro" value={fmtTime(selectedDay.workday.end_work_at)} />
                </div>
              ) : selectedDay.isWorkingDay ? (
                <div className="small">
                  Nessuna timbratura presente. Il giorno viene conteggiato come {absenceLabel.toLowerCase()}
                  {' '}per {fmtHM(selectedDay.absenceMin)}.
                </div>
              ) : (
                <div className="small">
                  Giorno non lavorativo. Non vengono conteggiate ore da contratto.
                </div>
              )}
            </div>

            <button
              className="btn btnSecondary"
              type="button"
              onClick={() => setDetailOpen(false)}
            >
              Chiudi
            </button>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function SummaryCard(props: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="small">
        {props.label}
      </div>

      <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.15 }}>
        {props.value}
      </div>

      {props.sub ? (
        <div className="small" style={{ marginTop: 4 }}>
          {props.sub}
        </div>
      ) : null}
    </div>
  );
}

function MiniStat(props: {
  label: string;
  value: string;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: 8,
        minWidth: 0
      }}
    >
      <div className="small">
        {props.label}
      </div>

      <div style={{ fontWeight: 900 }}>
        {props.value}
      </div>
    </div>
  );
}

function DetailRow(props: {
  label: string;
  value: string;
}) {
  return (
    <div className="spread" style={{ gap: 12, flexWrap: 'wrap' }}>
      <div className="small">
        {props.label}
      </div>

      <b>
        {props.value}
      </b>
    </div>
  );
}